import React, { useEffect, useMemo, useRef } from 'react';
import { cn } from '../../../utils/classnames';

const OtpInput = ({
  length = 6,
  value = '',
  onChange,
  onComplete, // dispara quando preenche tudo
  disabled = false,
  label,
  name = 'otp',
  required = false,
  autoFocus = true,
  labelClassName = '',
  inputClassName = '',
}) => {
  const digits = useMemo(() => (value || '').slice(0, length).split(''), [value, length]);
  const refs = useRef(Array.from({ length }, () => React.createRef()));

  useEffect(() => {
    if (autoFocus && refs.current[0]?.current) {
      refs.current[0].current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (onComplete && digits.length === length && digits.every((d) => d && d.length === 1)) {
      onComplete(value.slice(0, length));
    }
  }, [digits, length, onComplete, value]);

  const updateValue = (idx, char) => {
    const onlyDigit = char.replace(/\D/g, '').slice(0, 1);
    const next = Array.from({ length }, (_, i) => (i === idx ? onlyDigit : digits[i] || '')).join('');
    onChange?.({ target: { name, value: next } });
  };

  const handleChange = (idx) => (e) => {
    const char = e.target.value;
    const onlyDigit = char.replace(/\D/g, '').slice(-1); // pega último dígito digitado
    if (!onlyDigit) return;

    updateValue(idx, onlyDigit);

    // avança o foco imediatamente pro próximo input
    const nextRef = refs.current[idx + 1]?.current;
    if (nextRef) {
      nextRef.focus();
      nextRef.select();
    } else {
      e.target.blur();
    }
  };

  const handleKeyDown = (idx) => (e) => {
    if (e.key === 'Backspace') {
      if (!digits[idx]) {
        const prevRef = refs.current[idx - 1]?.current;
        if (prevRef) {
          const newValue = Array.from({ length }, (_, i) => (i === idx - 1 ? '' : digits[i] || '')).join('');
          onChange?.({ target: { name, value: newValue } });
          prevRef.focus();
          prevRef.select();
        }
      } else {
        const newValue = Array.from({ length }, (_, i) => (i === idx ? '' : digits[i] || '')).join('');
        onChange?.({ target: { name, value: newValue } });
      }
    }

    if (e.key === 'ArrowLeft') refs.current[idx - 1]?.current?.focus();
    if (e.key === 'ArrowRight') refs.current[idx + 1]?.current?.focus();
  };

  const handlePaste = (idx) => (e) => {
    const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();

    const merged = Array.from({ length }, (_, i) => {
      const fromPaste = text[i - idx] ?? '';
      return i < idx ? digits[i] || '' : fromPaste || digits[i] || '';
    })
      .join('')
      .slice(0, length);

    onChange?.({ target: { name, value: merged } });
    const lastIndex = Math.min(idx + text.length - 1, length - 1);
    refs.current[lastIndex]?.current?.focus();
    refs.current[lastIndex]?.current?.select();
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={`${name}-0`} className={cn('text-blueDarker font-normal', labelClassName)}>
          {label}
        </label>
      )}

      <div className="flex gap-3 justify-center">
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={refs.current[i]}
            id={`${name}-${i}`}
            name={`${name}-${i}`}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            required={required}
            disabled={disabled}
            value={digits[i] || ''}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={handlePaste(i)}
            onFocus={(e) => e.target.select()}
            className={cn(
              'border border-bluePale rounded-xl text-center text-2xl font-semibold text-blueDarker transition-all duration-100',
              'w-12 h-12 md:w-14 md:h-14',
              'focus:outline-none focus:ring-2 focus:ring-bluePale/60 focus:border-blueDarker',
              'placeholder-blueSteel',
              inputClassName
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default OtpInput;
